import React, { Children, useMemo, useCallback } from "react";
import {
  Chip,
  TableRow,
  TableCell,
  Typography,
  TableBody,
  styled,
  Box,
} from "@mui/material";
import { get } from "lodash";
import { useSelector } from "store";
import "./transactionTable.css";
import { Color } from "types/extended";
import { COMPLETE_WITHDRAWAL_STATUSES, FAILED_OR_PENDING_STATUSES, INCOMPLETE_WITHDRAWAL_STATUSES } from "utils/constants/transactions";
import { formatUTCDate } from "utils/CommonMethods";
import T from "utils/constants/T";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderTop: "inherit",
  overflow: "hidden",
  padding: "8px 0px 8px 10px",
}));

interface TransactionsTableBodyProps {
  allTableRows: any[];
  cs: string;
}
const TransactionsTableBody = ({
  allTableRows = [],
  cs,
}: TransactionsTableBodyProps) => {
  // Memoizing rows to avoid recalculating on every render
  const rows = useMemo(() => {
    return allTableRows.map((record, index) => {
      const status = get(record, "status", "PENDING");
      const transactionDate = get(record, "Date", "");
      const formattedDate = formatUTCDate(transactionDate);
      const payPeriod = get(record, "pay_periods", "");
      const systemPayrollId = get(record, "system_payroll_id", "N/A");
      const scheduleName = get(record, "schedule_details.payPeriodName", "");
      const employeeName = get(record, "employee_name", "");
      const companyName = get(record, "company_name", "");
      const transactionId = get(record, "transaction_id", "");
      const debit = get(record, "debit", "");
      const amount = get(record,"amount", 0);
      const fee = get(record, "fee", "");
     
      return {
        index,
        formattedDate,
        companyName,
        payPeriod,
        scheduleName,
        employeeName,
        systemPayrollId,
        transactionId,
        debit,
        amount,
        fee,
        status,
      };
    });
  }, [allTableRows]);

  // Function to render each row (memoized)
  const renderRow = useCallback((row: any) => {
    const {
      index,
      formattedDate,
      companyName,
      payPeriod,
      scheduleName,
      employeeName,
      systemPayrollId,
      transactionId,
      debit,
      fee,
      status,
      amount
    } = row;

    let statusColour: Color;

    if (FAILED_OR_PENDING_STATUSES.includes(status)) {
      statusColour = "error";
    } else if(INCOMPLETE_WITHDRAWAL_STATUSES.includes(status)) {
      statusColour = 'warning'
    } else if (COMPLETE_WITHDRAWAL_STATUSES.includes(status)) {
      statusColour = "success";
    } else {
      statusColour = "info";
    }

    return (
      <TableRow sx={{ background: "white" }} key={index}>
        <StyledTableCell>
          <Box className="table-column">{formattedDate}</Box>
        </StyledTableCell>
        <StyledTableCell>{companyName}</StyledTableCell>
        <StyledTableCell>
          <Box className="table-column">{payPeriod}</Box>
        </StyledTableCell>
        <StyledTableCell>{scheduleName || "N/A"}</StyledTableCell>
        <StyledTableCell>{employeeName}</StyledTableCell>
        <StyledTableCell>{systemPayrollId}</StyledTableCell>
        <StyledTableCell>{transactionId}</StyledTableCell>
        <StyledTableCell>{`${cs}` + debit}</StyledTableCell>
        <StyledTableCell>{`${cs}` + fee}</StyledTableCell>
        <StyledTableCell>
          <Chip
            color={statusColour}
            label={INCOMPLETE_WITHDRAWAL_STATUSES.includes(status) ? `${status} $${Number(amount).toFixed(2)}` : status}
            size="small"
            variant="light"
          />
        </StyledTableCell>
      </TableRow>
    );
  }, []);

  const { loader } = useSelector((state: any) => state.transactionData);

  return (
    <TableBody>
      {loader ? (
        <TableRow>
          <StyledTableCell colSpan={10} sx={{ border: "none" }}>
            <Typography variant="body1" textAlign="center">
              {T.LOADING}
            </Typography>
          </StyledTableCell>
        </TableRow>
      ) : allTableRows.length === 0 ? (
        <TableRow>
          <StyledTableCell colSpan={10} sx={{ border: "none" }}>
            <Typography variant="body1" textAlign="center">
              {T.NO_RECORDS}
            </Typography>
          </StyledTableCell>
        </TableRow>
      ) : (
        Children.toArray(rows.map(renderRow))
      )}
    </TableBody>
  );
};

export default React.memo(TransactionsTableBody);
